import React, { useState, useEffect } from 'react';
import { Plus, Trash2, RefreshCw, FileText, ClipboardList } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Loader from '../../components/ui/Loader';
import perfilPruebaService from '../../services/perfilPruebaService';
import perfilService from '../../services/perfilService';
import examenService from '../../services/examenService';

/**
 * Página para gestionar las asociaciones entre Perfiles y Pruebas (Raven y DISC)
 * - RAVEN: Exámenes dinámicos con preguntas creadas en backend
 * - DISC: Test estándar de 28 preguntas hardcoded en frontend
 */
export default function PerfilPruebas() {
  const [loading, setLoading] = useState(true);
  const [perfiles, setPerfiles] = useState([]);
  const [examenes, setExamenes] = useState([]);
  const [asociaciones, setAsociaciones] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPerfil, setSelectedPerfil] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [perfilesData, examenesData, asociacionesData] = await Promise.all([
        perfilService.getAllPerfiles(),
        examenService.getAllExamenes(),
        perfilPruebaService.getAll()
      ]);

      setPerfiles(perfilesData || []);
      setExamenes(examenesData || []);
      setAsociaciones(asociacionesData || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (perfil) => {
    setSelectedPerfil(perfil);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPerfil(null);
  };

  const handleAsociarExamenRaven = async (examenId) => {
    try {
      await perfilPruebaService.asociarExamenRaven(selectedPerfil.id, examenId);
      toast.success('Examen Raven asociado correctamente');
      loadData();
      closeModal();
    } catch (error) {
      console.error('Error al asociar examen:', error);
      toast.error('Error al asociar examen');
    }
  };

  const handleHabilitarDiscTest = async () => {
    try {
      // Ya existe un DiscExamen con ID=1 creado manualmente
      // Asociarlo directamente al perfil
      const discExamenMaestroId = 1;
      
      // Usar el método correcto según tu backend
      // Opción 1: Si el backend acepta discExamenId directamente
      await perfilPruebaService.asociarDiscExamen(selectedPerfil.id, discExamenMaestroId);
      
      toast.success('Test DISC habilitado para el perfil');
      loadData();
      closeModal();
    } catch (error) {
      console.error('Error al habilitar DISC:', error);
      toast.error(error.response?.data || 'Error al habilitar Test DISC');
    }
  };

  const handleEliminarAsociacion = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta asociación?')) return;

    try {
      await perfilPruebaService.delete(id);
      toast.success('Asociación eliminada');
      loadData();
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error('Error al eliminar');
    }
  };

  // Agrupar asociaciones por perfil
  const asociacionesPorPerfil = perfiles.map(perfil => {
    const pruebasPerfil = asociaciones.filter(a => a.perfilId === perfil.id);
    return {
      ...perfil,
      pruebas: pruebasPerfil
    };
  });

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-neutral-800">Perfil - Pruebas</h1>
          <p className="text-neutral-600 mt-1">Asocia exámenes Raven y Tests DISC a cada perfil</p>
        </div>
        <Button variant="outline" onClick={loadData} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      {/* Lista de perfiles con sus pruebas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {asociacionesPorPerfil.map((perfil) => (
          <div
            key={perfil.id}
            className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 space-y-4"
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-neutral-800">{perfil.nombre}</h3>
                <p className="text-sm text-neutral-600">{perfil.descripcion}</p>
              </div>
            </div>

            {/* Pruebas asociadas */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-neutral-500 uppercase">Pruebas Asignadas:</p>
              
              {perfil.pruebas.length === 0 ? (
                <p className="text-sm text-neutral-400 italic">Sin pruebas asignadas</p>
              ) : (
                <div className="space-y-2">
                  {perfil.pruebas.map((prueba) => {
                    // Detectar tipo según qué ID está presente
                    const esRaven = prueba.examenRavenId != null;
                    const esDisc = prueba.testDiscId != null;
                    
                    return (
                      <div
                        key={prueba.id}
                        className="flex items-center justify-between p-2 bg-neutral-50 rounded-md"
                      >
                        <div className="flex items-center gap-2">
                          {esRaven ? (
                            <>
                              <FileText className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-neutral-700">
                                Raven: {prueba.examenRavenNombre || `#${prueba.examenRavenId}`}
                              </span>
                            </>
                          ) : esDisc ? (
                            <>
                              <ClipboardList className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium text-neutral-700">
                                Test DISC (28 preguntas)
                              </span>
                            </>
                          ) : (
                            <span className="text-sm text-neutral-400 italic">Sin tipo definido</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleEliminarAsociacion(prueba.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Botón para agregar pruebas */}
            <Button
              onClick={() => openModal(perfil)}
              variant="outline"
              size="sm"
              className="w-full gap-2"
            >
              <Plus className="w-4 h-4" />
              Asignar Prueba
            </Button>
          </div>
        ))}
      </div>

      {/* Modal para asignar pruebas */}
      <AsignarPruebaModal
        isOpen={showModal}
        onClose={closeModal}
        perfil={selectedPerfil}
        examenes={examenes}
        onAsociarRaven={handleAsociarExamenRaven}
        onHabilitarDisc={handleHabilitarDiscTest}
      />
    </div>
  );
}

function AsignarPruebaModal({ isOpen, onClose, perfil, examenes, onAsociarRaven, onHabilitarDisc }) {
  const [tipoPrueba, setTipoPrueba] = useState('RAVEN'); // 'RAVEN' o 'DISC'
  const [pruebaSeleccionada, setPruebaSeleccionada] = useState(null);
  const [discHabilitado, setDiscHabilitado] = useState(false);

  if (!perfil) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (tipoPrueba === 'RAVEN') {
      if (!pruebaSeleccionada) {
        toast.error('Selecciona un examen Raven');
        return;
      }
      onAsociarRaven(pruebaSeleccionada);
    } else {
      // Para DISC: solo verificar que el checkbox esté marcado
      if (!discHabilitado) {
        toast.error('Marca el checkbox para habilitar Test DISC');
        return;
      }
      onHabilitarDisc();
    }

    setPruebaSeleccionada(null);
    setDiscHabilitado(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Asignar Prueba - ${perfil?.nombre}`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Selector de tipo de prueba */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Tipo de Prueba
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setTipoPrueba('RAVEN');
                setPruebaSeleccionada(null);
                setDiscHabilitado(false);
              }}
              className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                tipoPrueba === 'RAVEN'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <FileText className="w-5 h-5 mx-auto mb-1" />
              <span className="font-medium">Test Raven</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setTipoPrueba('DISC');
                setPruebaSeleccionada(null);
                setDiscHabilitado(false);
              }}
              className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                tipoPrueba === 'DISC'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <ClipboardList className="w-5 h-5 mx-auto mb-1" />
              <span className="font-medium">Test DISC</span>
            </button>
          </div>
        </div>

        {/* Selector de prueba específica */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            {tipoPrueba === 'RAVEN' ? 'Examen Raven' : 'Habilitar Test DISC'}
          </label>
          
          {tipoPrueba === 'RAVEN' ? (
            <select
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
              value={pruebaSeleccionada || ''}
              onChange={(e) => setPruebaSeleccionada(parseInt(e.target.value))}
              required
            >
              <option value="">Selecciona un examen</option>
              {examenes.map((examen) => (
                <option key={examen.id} value={examen.id}>
                  {examen.nombre || `Examen #${examen.id}`} - {examen.descripcion}
                </option>
              ))}
            </select>
          ) : (
            <>
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <ClipboardList className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <p className="font-semibold text-green-800 mb-1">Test DISC (28 Preguntas Fijas)</p>
                    <p className="text-xs text-green-700 mb-3">
                      El Test DISC <strong>ya existe en el frontend</strong> con 28 preguntas estandarizadas. 
                      Solo marca el checkbox para habilitarlo en este perfil.
                    </p>
                    
                    <label className="flex items-center gap-3 p-3 bg-white rounded-md border-2 border-green-300 cursor-pointer hover:bg-green-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={discHabilitado}
                        onChange={(e) => setDiscHabilitado(e.target.checked)}
                        className="w-5 h-5 text-green-600 border-green-300 rounded focus:ring-2 focus:ring-green-500"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-green-800">Habilitar Test DISC para {perfil?.nombre}</span>
                        <p className="text-xs text-green-600 mt-0.5">
                          Los usuarios con este perfil podran realizar el Test DISC de 28 preguntas
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-800">
                <p className="font-medium mb-1">Como funciona:</p>
                <ol className="ml-4 list-decimal space-y-1">
                  <li>Marcas el checkbox para habilitar DISC en este perfil</li>
                  <li>Los postulantes con este perfil veran el Test DISC disponible</li>
                  <li>Las 28 preguntas estan <strong>hardcoded en el frontend</strong></li>
                  <li>Al finalizar, se envia el resultado completo al backend via POST /api/disc/procesar</li>
                </ol>
              </div>
            </>
          )}
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            type="submit"
            disabled={tipoPrueba === 'RAVEN' ? !pruebaSeleccionada : !discHabilitado}
          >
            {tipoPrueba === 'RAVEN' ? 'Asociar Examen Raven' : 'Habilitar Test DISC'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
